import { useCallback, useState } from 'react';
import { API_V1, STICKERS, type ChatReportDecision, type ChatReportView } from '@london-life/contracts';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { Label } from '@london-life/ui/components/label';
import { Textarea } from '@london-life/ui/components/textarea';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { ReportedVoice } from '../lib/reported-voice';
import { useAdmin } from '../lib/admin-context';
import { when } from '../lib/format';
import { useRequest } from '../lib/use-request';

const KIND_LABEL = { direct: 'Direct chat', group: 'Group', place: 'Place chat' } as const;

// what a message said, in words an admin can read: stickers by their caption, removed ones marked
const messageText = (report: ChatReportView['message']) => {
  if (report.deletedAt && !report.text) return 'This message was removed.';
  if (report.kind === 'sticker' && report.text)
    return `Sticker: ${STICKERS[report.text as keyof typeof STICKERS] ?? report.text}`;
  return report.text ?? '—';
};

// moderators read what was reported with the messages around it, then remove it or dismiss the report.
// Every decision needs a recent two-step check and lands in the audit log.
export function ChatReportsPage() {
  const { can } = useAdmin();
  const [all, setAll] = useState(false);
  const reports = useRequest(useCallback(() => api.admin.chatReports(all), [all]));
  const [deciding, setDeciding] = useState<{ report: ChatReportView; outcome: ChatReportDecision['outcome'] } | null>(
    null,
  );
  const [note, setNote] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const decide = async () => {
    if (!deciding) return;
    await api.admin.decideChatReport(deciding.report.id, {
      outcome: deciding.outcome,
      ...(note.trim() ? { note: note.trim() } : {}),
    });
    setDone(deciding.outcome === 'removed' ? 'Message removed.' : 'Report dismissed.');
    await reports.reload();
  };
  const list = reports.data?.reports ?? [];
  return (
    <section>
      <PageHeader
        title="Chat reports"
        description="Messages players reported. Read them in context, then remove the message or dismiss the report."
      />
      <div className="admin-toolbar">
        <Button type="button" variant={all ? 'outline' : 'default'} onClick={() => setAll(false)}>
          Open{reports.data ? ` (${reports.data.open})` : ''}
        </Button>
        <Button type="button" variant={all ? 'default' : 'outline'} onClick={() => setAll(true)}>
          All reports
        </Button>
      </div>
      {reports.error && <ErrorNotice error={reports.error} onRetry={() => void reports.reload()} />}
      {done && (
        <p role="status" className="admin-muted">
          {done}
        </p>
      )}
      {reports.busy && !reports.data && <p className="admin-muted">Loading…</p>}
      {reports.data && list.length === 0 && (
        <div className="admin-empty">
          <strong className="admin-empty-title">{all ? 'No reports yet.' : 'Nothing to review.'}</strong>
        </div>
      )}
      <ul className="admin-reports">
        {list.map((report) => (
          <li key={report.id} className="admin-panel" data-testid="chat-report">
            <div className="admin-player-head">
              <h2>
                {report.sender.name} <span className="admin-muted">reported by {report.reporter.name}</span>
              </h2>
              {report.resolvedAt ? (
                <Badge variant="secondary">{report.outcome === 'removed' ? 'Removed' : 'Dismissed'}</Badge>
              ) : (
                <Badge>Open</Badge>
              )}
            </div>
            <dl className="admin-facts">
              <dt>Reason</dt>
              <dd>{report.reason}</dd>
              <dt>Where</dt>
              <dd>{KIND_LABEL[report.message.conversationKind]}</dd>
              <dt>Reported</dt>
              <dd>{when(report.createdAt)}</dd>
              <dt>Message</dt>
              <dd>
                {report.message.kind === 'voice' ? (
                  // the copy kept aside when it was reported
                  <ReportedVoice path={API_V1.adminChatReportVoice(report.id)} />
                ) : (
                  <strong>{messageText(report.message)}</strong>
                )}
              </dd>
              {report.resolvedAt && (
                <>
                  <dt>Decided</dt>
                  <dd>{when(report.resolvedAt)}</dd>
                </>
              )}
            </dl>
            {report.context.length > 0 && (
              <ol className="admin-chat-context" aria-label="Messages around it">
                {report.context.map((line, index) => (
                  <li key={`${line.createdAt}-${index}`} className={line.reported ? 'reported' : undefined}>
                    <strong>{line.sender}</strong> <span>{line.text ?? 'removed'}</span>{' '}
                    <small className="admin-muted">{when(line.createdAt)}</small>
                  </li>
                ))}
              </ol>
            )}
            {!report.resolvedAt && can('players.support') && (
              <div className="admin-page-actions wrap">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    setNote('');
                    setDone(null);
                    setDeciding({ report, outcome: 'removed' });
                  }}
                >
                  Remove message
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setNote('');
                    setDone(null);
                    setDeciding({ report, outcome: 'dismissed' });
                  }}
                >
                  Dismiss
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <FormDialog
        open={!!deciding}
        title={deciding?.outcome === 'removed' ? 'Remove this message' : 'Dismiss this report'}
        description={
          deciding?.outcome === 'removed'
            ? 'The message is removed for everyone in the chat. Other open reports on it are closed too.'
            : 'The message stays. The report is closed.'
        }
        submitLabel={deciding?.outcome === 'removed' ? 'Remove' : 'Dismiss'}
        onSubmit={decide}
        onClose={() => setDeciding(null)}
      >
        <div className="admin-field">
          <Label htmlFor="report-note">Note for the audit log (optional)</Label>
          <Textarea id="report-note" maxLength={300} value={note} onChange={(event) => setNote(event.target.value)} />
        </div>
      </FormDialog>
    </section>
  );
}
